import {
  PolfEventModeEnum,
  PolfEventPokerStatusEnum,
  PolfEventStatusEnum,
} from '@polf/shared/codegen/types'
import { useLivePoll } from '@polf/shared/hooks'
import { useEffect, useRef } from 'react'
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'

import {
  useCurrentPlayerPokerViewQuery,
  useCurrentPlayerQuery,
} from './__generated__/hooks'
import BottomNav, { type Phase } from './components/BottomNav'
import TopBar from './components/TopBar'
import UpdatePrompt from './components/UpdatePrompt'
import { useOnlineStatus } from './hooks/useOnlineStatus'
import { useStaleBundleRecovery } from './hooks/useStaleBundleRecovery'
import { forceAppUpdate } from './pwa'
import { isEventOverError, isSchemaMismatchError } from './util/apollo-error'
import { modeEyebrow } from './util/event-mode'

export type { Phase }

/** No interaction for this long steps every player poll down to a slow tick. */
const IDLE_AFTER_MS = 5 * 60_000
/** Routes whose page owns a faster poll of currentPlayerPokerView. */
const POKER_POLL_PATHS = ['/poker', '/payouts', '/stack']

/**
 * Shell wrapping all signed-in player routes (`/s/:playerKey/*`).
 *
 * Polls currentPlayer and exposes it — plus its `refetch` — via React Router's
 * Outlet context, so a phase change reaches the player without a refresh. Phase derivation reads
 * BOTH the golf axis and the poker phase status — golf being final on a
 * POLF event keeps GOLF tabs until the poker phase actually transitions to
 * SEATED/RUNNING. That avoids the "the round is done but the dealer hasn't
 * drawn yet" gap showing the wrong tab set.
 */
export type CurrentPlayerCtx = NonNullable<
  ReturnType<typeof useCurrentPlayerQuery>['data']
>['currentPlayer']

const PlayerShell = () => {
  const location = useLocation()
  const { playerKey = '' } = useParams<{ playerKey: string }>()
  const online = useOnlineStatus()
  const { data, loading, error, refetch } = useCurrentPlayerQuery({
    fetchPolicy: 'cache-and-network',
  })

  // A guard rejection with nothing cached is terminal — an invalid key or
  // expired access won't change on re-poll, so stop hammering the API from a
  // dead link left open in a tab. Network errors carry no graphQLErrors and
  // keep polling; that's how the app recovers from connectivity blips. A
  // schema mismatch (stale bundle) also keeps polling: recovery reloads onto
  // the new build, and if that fails the poll heals the session once the
  // waiting SW eventually takes over.
  const staleBundle = !data?.currentPlayer && isSchemaMismatchError(error)
  const recovery = useStaleBundleRecovery(staleBundle, !!data?.currentPlayer)
  const rejected =
    !data?.currentPlayer &&
    (error?.graphQLErrors.length ?? 0) > 0 &&
    !staleBundle
  // Status pill / phase derivation only. The poker pages poll this same query
  // faster; the cadences are wired together below.
  const {
    data: pokerData,
    error: pokerError,
    refetch: refetchPoker,
  } = useCurrentPlayerPokerViewQuery({
    fetchPolicy: 'cache-and-network',
    skip: !data?.currentPlayer?.polfEvent?.hasPokerPhase,
  })

  // Transition redirect: the moment golf flips to poker (dealer draws seats),
  // take the player to the poker home ONCE — even from the read-only golf
  // archive (scorecard/leaderboard), which otherwise stays reachable during
  // poker and so isn't caught by the deep-link redirect below. Mirrors the
  // `phase` derivation further down; kept here (before the guard returns) so
  // the effect's hook order is stable.
  const navigate = useNavigate()
  const evt = data?.currentPlayer?.polfEvent
  const livePokerStatus = pokerData?.currentPlayerPokerView?.status ?? null
  // A poker-only (POKER) event has no golf phase — it is always POKER, even
  // before seats are drawn (the poker view shows the pre-draw waiting state).
  const liveIsPokerOnly = evt?.mode === PolfEventModeEnum.Poker
  const livePhase: Phase =
    liveIsPokerOnly ||
    (evt?.golfFinishedAt != null &&
      evt?.hasPokerPhase &&
      livePokerStatus !== null &&
      livePokerStatus !== PolfEventPokerStatusEnum.NotStarted)
      ? 'POKER'
      : 'GOLF'
  // Cadence by relevance: the player record only moves minute-to-minute while
  // golf is still playable. A rejected key can only stay rejected, so it stops
  // polling entirely; golf-final keeps a slow tick because a TD may reopen the
  // round to correct a score.
  const golfLive = evt != null && !evt.isReadOnly
  useLivePoll(refetch, golfLive ? 15_000 : 60_000, !rejected && !staleBundle, {
    idleAfterMs: IDLE_AFTER_MS,
    idleIntervalMs: 60_000,
  })
  // The poker pages own a 3s poll of this same query and write to the same
  // cache entry, so the shell only polls it while they are unmounted.
  const pokerPageOwnsPoll = POKER_POLL_PATHS.some((path) =>
    location.pathname.endsWith(path)
  )
  useLivePoll(
    refetchPoker,
    livePokerStatus === PolfEventPokerStatusEnum.Finished ? 60_000 : 10_000,
    !!evt?.hasPokerPhase && !pokerPageOwnsPoll && !rejected,
    { idleAfterMs: IDLE_AFTER_MS, idleIntervalMs: 60_000 }
  )
  const dataReady =
    !!data?.currentPlayer &&
    (!evt?.hasPokerPhase || !!pokerData || !!pokerError)
  const prevPhaseRef = useRef<Phase | null>(null)
  useEffect(() => {
    if (!dataReady) return
    const prev = prevPhaseRef.current
    prevPhaseRef.current = livePhase
    // Only a real in-session GOLF→POKER change navigates; the first ready
    // render just records the phase (prev === null) so a fresh load into an
    // already-poker event doesn't yank the player off a deep-linked page.
    if (prev === 'GOLF' && livePhase === 'POKER') {
      navigate(`/s/${playerKey}/poker`, { replace: true })
    }
  }, [dataReady, livePhase, playerKey, navigate])

  if (loading && !data) {
    return (
      <main className="min-h-dvh flex items-center justify-center">
        <p className="text-ink-500">Laster…</p>
      </main>
    )
  }

  // Only a MISSING player is an invalid link. Now that currentPlayer polls, a
  // transient error (network blip, or a momentary backend race while the poker
  // phase is being enabled) must NOT blank the whole app — Apollo keeps the
  // last good `data` across a failed refetch, so we keep rendering it. Blanking
  // on `error` here flashed "Spillerlenke ikke gyldig" the instant poker was
  // turned on, even though the cached player was still valid.
  if (!data?.currentPlayer) {
    // A stale precached bundle whose query the current schema rejects — the
    // key may be perfectly valid, so this must win over the invalid-link
    // fallback. Recovery force-updates + reloads once; if this load IS the
    // failed attempt, offer the update manually instead.
    if (staleBundle) {
      if (recovery === 'failed') {
        return (
          <main className="min-h-dvh flex flex-col items-center justify-center px-6 text-center gap-4">
            <h1 className="font-display text-xl text-green-900">
              Appen må oppdateres
            </h1>
            <p className="text-ink-500 text-sm">
              En ny versjon av appen er klar. Trykk for å laste den inn på nytt.
            </p>
            <button
              type="button"
              onClick={() => forceAppUpdate(3000)}
              className="rounded-xl px-6 py-3 font-display font-semibold text-sm bg-green-700 text-paper active:bg-green-900 transition-colors"
              style={{ minHeight: 44 }}
            >
              Oppdater appen
            </button>
          </main>
        )
      }
      return (
        <main className="min-h-dvh flex flex-col items-center justify-center px-6 text-center gap-2">
          <h1 className="font-display text-xl text-green-900">
            Oppdaterer appen
          </h1>
          <p className="text-ink-500 text-sm">
            En ny versjon lastes inn automatisk …
          </p>
        </main>
      )
    }
    // A closed event / expired keyed access is NOT a bad link — asking for a
    // new SMS link would change nothing, so say what actually happened.
    if (isEventOverError(error)) {
      return (
        <main className="min-h-dvh flex flex-col items-center justify-center px-6 text-center gap-2">
          <h1 className="font-display text-xl text-green-900">
            Turneringen er avsluttet
          </h1>
          <p className="text-ink-500 text-sm">Lenken er ikke lenger aktiv.</p>
        </main>
      )
    }
    return (
      <main className="min-h-dvh flex flex-col items-center justify-center px-6 text-center gap-4">
        <h1 className="font-display text-xl text-flag">
          Spillerlenke ikke gyldig
        </h1>
        <p className="text-ink-500 text-sm">
          Sjekk at du åpnet den nyeste lenken du fikk på SMS.
        </p>
        <button
          type="button"
          onClick={() => forceAppUpdate(3000)}
          className="rounded-xl px-6 py-3 font-display font-semibold text-sm bg-green-700 text-paper active:bg-green-900 transition-colors"
          style={{ minHeight: 44 }}
        >
          Prøv på nytt
        </button>
      </main>
    )
  }

  // If the event has poker enabled we can't derive phase = POKER until the
  // poker query resolves (its `status` is what flips phase). Hold the shell on
  // the loader until poker data lands rather than rendering golf content.
  //
  // Gating on `!pokerData` (not `loading`) is deliberate: when the poker
  // query un-skips mid-session, Apollo can report `loading: false` for a tick
  // before the fetch starts — in that tick the golf Score page would flash
  // (phase still GOLF, poker status unknown), stranding a player on /score
  // with the wrong banner/nav until refresh. This makes the in-session
  // transition behave like a fresh mount. Fall through on error so a failed
  // poker query can't trap the player on the loader forever.
  const needsPokerData = data.currentPlayer.polfEvent.hasPokerPhase
  if (needsPokerData && !pokerData && !pokerError) {
    return (
      <main className="min-h-dvh flex items-center justify-center">
        <p className="text-ink-500">Laster…</p>
      </main>
    )
  }

  const player = data.currentPlayer
  const pokerView = pokerData?.currentPlayerPokerView
  const pokerStatus = pokerView?.status ?? null
  // Show the "Premier" tab only when the event actually has payouts configured.
  const hasPayouts = !!(pokerView?.usePayouts && pokerView.payouts.length > 0)

  // Phase: POKER iff golf is FINAL and a poker phase is past NOT_STARTED.
  // Until the dealer presses "draw seats" the player still sees Golf tabs
  // (Score / Scorecard / etc.) even after golf finishes.
  const isPokerOnly = player.polfEvent.mode === PolfEventModeEnum.Poker
  const phase: Phase =
    isPokerOnly ||
    (player.polfEvent.golfFinishedAt != null &&
      player.polfEvent.hasPokerPhase &&
      pokerStatus !== null &&
      pokerStatus !== PolfEventPokerStatusEnum.NotStarted)
      ? 'POKER'
      : 'GOLF'

  // Hard phase redirect. Score entry is golf-only (meaningless once poker is
  // live), and the poker seat / stack views are poker-only. A deep link that
  // lands in the wrong phase — e.g. the golf SMS pointing at /score after the
  // dealer has drawn seats — is bounced to the active phase's home so the
  // content matches the nav (the nav already follows `phase`). Read-only golf
  // archive (scorecard / leaderboard / sidegames) stays reachable during poker
  // by design, so it is intentionally NOT redirected.
  //
  // The phase-exclusive routes below (`score` for golf, `poker`/`stack` for
  // poker) mirror BottomNav's per-phase tab sets — keep the two in sync when
  // adding a route that only belongs to one phase.
  const sub = location.pathname.split('/').filter(Boolean).pop()
  let redirectTo: string | null = null
  if (phase === 'POKER' && sub === 'score') {
    redirectTo = `/s/${playerKey}/poker`
  } else if (phase === 'GOLF' && (sub === 'poker' || sub === 'stack')) {
    redirectTo = `/s/${playerKey}/score`
  } else if (
    isPokerOnly &&
    (sub === 'profile' ||
      sub === 'scorecard' ||
      sub === 'sidegames' ||
      sub === 'leaderboard')
  ) {
    // A poker-only event has no golf surfaces at all — profile and the golf
    // archive are meaningless (and their queries assume a course). The chip
    // leaderboard lives on /stack.
    redirectTo = `/s/${playerKey}/${sub === 'leaderboard' ? 'stack' : 'poker'}`
  }

  // Status pill: derived from the golf axis + poker state, not from phase
  // alone. A POLF event stays lifecycle-STARTED through the poker phase, so
  // "golf is over" comes from golfFinishedAt — the pill flips to FERDIG the
  // moment scoring locks, exactly as it did when FINISHED meant golf-done.
  let statusLabel: 'LIVE' | 'POKER' | 'FERDIG' | 'PAUSE'
  if (phase === 'POKER') {
    if (pokerStatus === PolfEventPokerStatusEnum.Paused) statusLabel = 'PAUSE'
    else if (pokerStatus === PolfEventPokerStatusEnum.Finished)
      statusLabel = 'FERDIG'
    else statusLabel = 'POKER'
  } else if (
    player.polfEvent.status === PolfEventStatusEnum.Started &&
    player.polfEvent.golfFinishedAt == null
  ) {
    statusLabel = 'LIVE'
  } else {
    statusLabel = 'FERDIG'
  }

  return (
    <div className="min-h-dvh flex flex-col bg-cream-bg">
      <TopBar
        eventName={player.polfEvent.name}
        playerName={player.name}
        hcp={player.hcp}
        modeLabel={modeEyebrow(player.polfEvent.mode)}
        statusLabel={statusLabel}
        offline={!online}
      />
      <main
        className="flex-1 pb-bottom-nav"
        style={{
          paddingBottom: `calc(var(--bottom-nav-h) + env(safe-area-inset-bottom))`,
        }}
      >
        {redirectTo ? (
          <Navigate to={redirectTo} replace />
        ) : (
          <Outlet
            context={
              { player, phase, refetchPlayer: refetch } satisfies {
                player: CurrentPlayerCtx
                phase: Phase
                refetchPlayer: typeof refetch
              }
            }
          />
        )}
      </main>
      <UpdatePrompt />
      <BottomNav
        phase={phase}
        showPremier={hasPayouts}
        pokerOnly={isPokerOnly}
      />
    </div>
  )
}

export default PlayerShell
